import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { Dynamic, type DynamicAction } from '@shared/registry';

import { type VisitCardConfig, type VisitCardTitle } from './visit-card.types';

@Component({
  selector: 'app-visit-card',
  templateUrl: './visit-card.html',
  imports: [Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisitCard {
  readonly config = input.required<VisitCardConfig>();

  readonly action = output<DynamicAction>();

  protected readonly isHighlighted = computed(() => this.config().variant === 'highlighted');

  protected readonly chipConfig = computed(() => {
    const chip = this.config().chip;
    if (!chip) return null;
    return {
      ...chip,
      variant: this.isHighlighted() ? 'subtle' : chip.variant,
    };
  });

  protected readonly containerBackground = computed(() => {
    if (this.isHighlighted()) {
      return 'linear-gradient(180deg, var(--gradients-tertiary-start), var(--gradients-tertiary-middle), var(--gradients-tertiary-stop))';
    }
    return 'var(--visitcard-idle-background)';
  });

  protected readonly defaultTitleColor = computed(() =>
    this.isHighlighted() ? 'var(--visitcard-highlighted-content)' : 'var(--visitcard-idle-content)',
  );

  protected readonly normalizedTitles = computed<VisitCardTitle[]>(() => {
    const defaultColor = this.defaultTitleColor();
    return this.config().titles.map((title) => {
      if (typeof title === 'string') {
        return { text: title, color: defaultColor };
      }
      return { ...title, color: title.color ?? defaultColor };
    });
  });

  protected onClose(): void {
    this.action.emit(this.config().dismissAction ?? { id: 'dismiss' });
  }

  protected onButtonClick(event: DynamicAction): void {
    this.action.emit(event);
  }
}
